import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

// Loading Components
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageLoader } from '@/shared/components/PageLoader';

// Layouts (not lazy-loaded - needed immediately)
import MainLayout from '@/shared/components/layout/MainLayout';

// Lazy-loaded Pages
const ThemedLoginRouter = lazy(() => import('@/features/auth/pages/ThemedLoginRouter'));
const BasicLogin = lazy(() => import('@/features/auth/pages/BasicLoginPage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Users = lazy(() => import('./pages/users/Users'));
const Resources = lazy(() => import('./pages/resources/Resources'));
const Settings = lazy(() => import('@/features/settings/pages/Settings'));

// Lazy-loaded Settings Pages
const SettingsLayout = lazy(() => import('@/features/settings/pages/SettingsLayout'));
const ProfileSettings = lazy(() => import('@/features/settings/pages/ProfileSettings'));
const PluginsSettings = lazy(() => import('@/features/settings/pages/PluginsSettings'));

// Lazy-loaded About Page
const AboutPage = lazy(() => import('./pages/about/AboutPage'));

// Lazy-loaded Booking Pages
const RoomsPage = lazy(() => import('@/features/bookings/pages/RoomsPage'));
const MyBookingsPage = lazy(() => import('@/features/bookings/pages/MyBookingsPage'));
const EquipmentBookingPage = lazy(() => import('@/features/bookings/pages/EquipmentBookingPage'));

// Lazy-loaded Calendar Pages
const CalendarLayout = lazy(() => import('@/features/calendar/pages/CalendarLayout'));
const CalendarView = lazy(() => import('@/features/calendar/CalendarView'));
const RoomsCalendarView = lazy(() => import('@/features/calendar/pages/RoomsCalendarView'));
const EquipmentCalendarView = lazy(() => import('@/features/calendar/pages/EquipmentCalendarView'));

// Lazy-loaded Admin Pages
const AdminLayout = lazy(() => import('@/features/admin/pages/AdminLayout'));
const PendingApprovalsPage = lazy(() => import('@/features/admin/pages/PendingApprovalsPage'));
const UsersManagementPage = lazy(() => import('@/features/admin/pages/UsersManagementPage'));
const RoomsManagementPage = lazy(() => import('@/features/admin/pages/RoomsManagementPage'));
const EquipmentManagementPage = lazy(() => import('@/features/admin/pages/EquipmentManagementPage'));
const StaffManagementPage = lazy(() => import('@/features/admin/pages/StaffManagementPage'));
const ExpertiseManagementPage = lazy(() => import('@/features/admin/pages/ExpertiseManagementPage'));
const ActivityLogPage = lazy(() => import('@/features/admin/pages/ActivityLogPage'));
const GoogleCalendarStatusPage = lazy(() => import('@/features/admin/pages/GoogleCalendarStatusPage'));
const CalendarSettingsPage = lazy(() => import('@/features/admin/pages/CalendarSettingsPage'));
const AppearanceAdminPage = lazy(() => import('@/features/admin/pages/AppearanceAdminPage'));
const PluginsManagementPage = lazy(() => import('@/features/admin/pages/PluginsManagementPage'));
const AdminDashboardPage = lazy(() => import('@/features/admin/pages/AdminDashboardPage'));

// Lazy-loaded Dashboard Pages
const NotificationsPage = lazy(() => import('@/features/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

// Components
import { ProtectedPluginRoute } from '@/features/admin/components/ProtectedPluginRoute';
import { AdminRoute } from '@/features/admin/components/AdminRoute';

// Context Providers
import { SystemSettingsProvider } from './contexts/SystemSettingsProvider';
import { ThemeProvider } from './contexts/ThemeProvider';
import { LayoutProvider } from './contexts/LayoutContext';
import { PluginProvider } from './contexts/PluginContext';

// Hooks
import { useAuthStore } from '@/features/auth';

// Config
import { ROUTES, DEFAULT_AUTHENTICATED_ROUTE, DEFAULT_UNAUTHENTICATED_ROUTE } from './config/routes';

// Styles
import './styles/globals.css';

// Initialize i18n
import './i18n';

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Return Navigate without wrapping to prevent blank screens
  if (!isAuthenticated) {
    return <Navigate to={DEFAULT_UNAUTHENTICATED_ROUTE} replace />;
  }

  return <>{children}</>;
}

// Public Route Wrapper (redirects to my-bookings if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Return Navigate without wrapping to prevent blank screens
  if (isAuthenticated) {
    return <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />;
  }

  return <>{children}</>;
}

function App() {
  // Log app version, commit hash, and branch on mount
  useEffect(() => {
    const version = '1.0.10'; // From package.json
    const commitHash = import.meta.env.VITE_GIT_COMMIT || 'unknown';
    const branch = import.meta.env.VITE_GIT_BRANCH || 'unknown';

    console.log(`
╔═══════════════════════════════════════════════════════════╗
║           🚀 Chamama Booking App                          ║
╠═══════════════════════════════════════════════════════════╣
║  📦 Version: ${version.padEnd(44)} ║
║  🔖 Commit:  ${commitHash.substring(0, 7).padEnd(44)} ║
║  🌿 Branch:  ${branch.padEnd(44)} ║
╚═══════════════════════════════════════════════════════════╝
    `);
  }, []);

  // Get auth state for routing
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <SystemSettingsProvider>
      <ThemeProvider>
        <LayoutProvider>
          <PluginProvider>
            <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Routes key={isAuthenticated ? 'authenticated' : 'public'}>
                {/* Public Routes */}
                <Route
                  path={ROUTES.LOGIN}
                  element={
                    <PublicRoute>
                      <Suspense fallback={<LoadingSpinner />}>
                        <ThemedLoginRouter />
                      </Suspense>
                    </PublicRoute>
                  }
                />
                <Route
                  path="/basicLogin"
                  element={
                    <PublicRoute>
                      <Suspense fallback={<LoadingSpinner />}>
                        <BasicLogin />
                      </Suspense>
                    </PublicRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path={ROUTES.ROOT}
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />} />
                  <Route
                    path="dashboard"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Dashboard />
                      </Suspense>
                    }
                  />
                  <Route
                    path="dashboard/notifications"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <NotificationsPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Users />
                      </Suspense>
                    }
                  />
                  <Route
                    path="resources"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <Resources />
                      </Suspense>
                    }
                  />

                  {/* Booking Routes (Plugin-Protected) */}
                  <Route
                    path="bookings/rooms"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <RoomsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/equipment"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentBookingPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/my-bookings"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <MyBookingsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  <Route
                    path="bookings/my-bookings/:bookingId"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <MyBookingsPage />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  />
                  {/* Calendar Routes (Nested with query params for week + booking modal routes) */}
                  <Route
                    path="calendar"
                    element={
                      <ProtectedPluginRoute pluginType="BOOKING">
                        <Suspense fallback={<PageLoader />}>
                          <CalendarLayout />
                        </Suspense>
                      </ProtectedPluginRoute>
                    }
                  >
                    <Route
                      index
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CalendarView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="rooms"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <RoomsCalendarView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="rooms/booking/:bookingId"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <RoomsCalendarView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="equipment"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentCalendarView />
                        </Suspense>
                      }
                    />
                    <Route
                      path="equipment/booking/:bookingId"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentCalendarView />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Admin Routes (ADMIN role only - Nested with RBAC tab filtering) */}
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <AdminLayout />
                        </Suspense>
                      </AdminRoute>
                    }
                  >
                    <Route index element={<Navigate to="approvals" replace />} />
                    <Route
                      path="approvals"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PendingApprovalsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="approvals/:bookingId"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PendingApprovalsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <UsersManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="rooms"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <RoomsManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="equipment"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <EquipmentManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="staff"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <StaffManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="expertises"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExpertiseManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="activity"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ActivityLogPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="calendar"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <GoogleCalendarStatusPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="calendar-settings"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CalendarSettingsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="appearance"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AppearanceAdminPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="plugins"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PluginsManagementPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="statistics"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AdminDashboardPage />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* About Page */}
                  <Route
                    path="about"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <AboutPage />
                      </Suspense>
                    }
                  />

                  {/* Settings with nested routes */}
                  <Route
                    path="settings"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <SettingsLayout />
                      </Suspense>
                    }
                  >
                    <Route index element={<Navigate to={ROUTES.SETTINGS_PROFILE} replace />} />
                    <Route
                      path="profile"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ProfileSettings />
                        </Suspense>
                      }
                    />
                    <Route
                      path="plugins"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PluginsSettings />
                        </Suspense>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Settings />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                {/* Catch all - redirect to my-bookings */}
                <Route path="*" element={<Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />} />
              </Routes>
            </BrowserRouter>

            {/* Toast Notifications - Using custom toast component with X button */}
            <Toaster
              position="top-center"
              containerStyle={{
                top: '80px', // Below the header to not hide user menu
              }}
              toastOptions={{
                duration: 4000,
              }}
            />

            {/* React Query Devtools (development only) */}
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
        </PluginProvider>
      </LayoutProvider>
    </ThemeProvider>
    </SystemSettingsProvider>
  );
}

export default App;
